import { AlertTriangle, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@/lib/api';
import { Button } from './Button';
import { Spinner } from './Spinner';
import styles from './States.module.css';

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.state}>
      <Spinner size={22} label={label ?? t('common.loading')} />
    </div>
  );
}

export function EmptyState({
  title,
  body,
  icon,
  action,
}: {
  title: ReactNode;
  body?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={styles.state}>
      <div className={styles.icon}>{icon ?? <Inbox size={22} />}</div>
      <p className={styles.title}>{title}</p>
      {body && <p className={styles.body}>{body}</p>}
      {action}
    </div>
  );
}

/** Shows an API/problem-details error with the requestId staff can quote. */
export function ErrorState({
  error,
  title,
  body,
  onRetry,
}: {
  error?: unknown;
  title?: ReactNode;
  body?: ReactNode;
  /** `null` marks a terminal error for which retrying cannot help. */
  onRetry?: (() => void) | null;
}) {
  const { t } = useTranslation();
  const apiError = isApiError(error) ? error : undefined;
  const retry =
    onRetry === undefined ? () => window.location.reload() : onRetry;
  return (
    <div className={[styles.state, styles.errorState].join(' ')} role="alert">
      <div className={[styles.icon, styles.danger].join(' ')}>
        <AlertTriangle size={22} />
      </div>
      <p className={styles.title}>
        {title ?? apiError?.problem?.title ?? t('errors.genericTitle')}
      </p>
      <p className={styles.body}>
        {body ?? apiError?.message ?? t('errors.genericBody')}
      </p>
      <p className={styles.code}>
        {t('common.problemCode', {
          code: apiError?.code ?? t('errors.unknownCode'),
        })}
      </p>
      {apiError?.requestId && (
        <p className={styles.reference}>
          {t('common.requestId', { id: apiError.requestId })}
        </p>
      )}
      {retry && (
        <Button size="sm" variant="danger" onClick={retry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}
